import i18n from "i18n";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Route,
  RouterProvider,
} from "react-router-dom";
import { me, selectAuthInfo } from "store/authSlice";
import { fetchUser, fetchUserProfile, selectUserInfo } from "store/userSlice";
import useSessionKeepAlive from "hooks/useSessionKeepAlive";

import Index from "containers/Index";
import Layout from "layout";

import AdminLayout from "containers/admin/AdminLayout";
import BoardDetails from "containers/admin/boards/BoardDetails/BoardDetails";
import BoardDetailsLayout from "containers/admin/boards/BoardDetails/BoardDetailsLayout";
import BoardReleases from "containers/admin/boards/BoardDetails/Releases";
import BoardRoadmap from "containers/admin/boards/BoardDetails/Roadmap";
import BoardSettings from "containers/admin/boards/BoardDetails/Settings";
import BoardWidget from "containers/admin/boards/BoardDetails/Widget";
import BoardsList from "containers/admin/boards/BoardsList";
import Dashboard from "containers/admin/Dashboard";
import Contacts from "containers/admin/messaging/Contacts";
import MessagingLayout from "containers/admin/messaging/MessagingLayout";
import NewSmsJob from "containers/admin/messaging/NewSmsJob";
import SmsSending from "containers/admin/messaging/SmsSending";
import ProfileSettings from "containers/admin/ProfileSettings";
import CompanyMembers from "containers/admin/settings/CompanyMembers";
import CompanySettings from "containers/admin/settings/CompanySettings";
import SettingsLayout from "containers/admin/settings/SettingsLayout";
import Subscription from "containers/admin/Subscription";
import UsersList from "containers/admin/UsersList";

import LoginForm from "containers/LoginForm";
import LogoutForm from "containers/LogoutForm";
import ResetPasswordForm from "containers/ResetPasswordForm";
import ResetPasswordNew from "containers/ResetPasswordNew";
import ResetPasswordSent from "containers/ResetPasswordSent";
import ResetPasswordToken from "containers/ResetPasswordToken";
import SignupForm from "containers/SignupForm";

import NotFound from "containers/NotFound";
import PublicView from "containers/PublicView";

function App() {
  const dispatch = useDispatch();
  const authInfo = useSelector(selectAuthInfo);
  const userInfo = useSelector(selectUserInfo);
  const [isLanguageReady, setIsLanguageReady] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (token) {
      dispatch(me());
    } else {
      // If no token, user is not logged in, so we can show the app immediately
      setIsLanguageReady(true);
    }
  }, [dispatch]);

  // Fetch user profile and set language when user is authenticated
  useEffect(() => {
    if (authInfo.isUserLoggedIn) {
      dispatch(fetchUserProfile());
      dispatch(fetchUser());
    }
  }, [dispatch, authInfo.isUserLoggedIn]);

  // Keep an active session's JWT refreshed so idle time, not elapsed time,
  // determines when the user gets logged out
  useSessionKeepAlive(authInfo.isUserLoggedIn);

  // Set application language based on user profile on initial load
  useEffect(() => {
    if (userInfo.lang && !isLanguageReady) {
      const langCode = userInfo.lang.split("-")[0]; // Convert en-US to en, pl-PL to pl
      i18n.changeLanguage(langCode);
      setIsLanguageReady(true);
    } else if (authInfo.isUserLoggedIn && userInfo.status === "succeeded") {
      // User is logged in but no language set, use default and mark ready
      setIsLanguageReady(true);
    }
  }, [
    userInfo.lang,
    authInfo.isUserLoggedIn,
    userInfo.status,
    isLanguageReady,
  ]);

  // Show loader while waiting for language to be set
  if (!isLanguageReady) {
    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "#ffffff",
          zIndex: 9999,
        }}
      >
        <div
          style={{
            width: "40px",
            height: "40px",
            border: "4px solid #f3f3f3",
            borderTop: "4px solid #3498db",
            borderRadius: "50%",
            animation: "spin 1s linear infinite",
          }}
        ></div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  const router = createBrowserRouter(
    createRoutesFromElements(
      <Route element={<Layout showMenuToolbar={authInfo.isUserLoggedIn} />}>
        <Route path="/" element={<Index />} />
        <Route path="/login" element={<LoginForm />} />
        <Route path="/logout" element={<LogoutForm />} />
        <Route path="/signup" element={<SignupForm />} />
        <Route path="/signup/:ref" element={<SignupForm />} />

        <Route path="/password-reset">
          <Route index element={<ResetPasswordForm />} />
          <Route path="sent" element={<ResetPasswordSent />} />
          <Route path="new/:email/:token" element={<ResetPasswordNew />} />
          <Route path=":email/:token" element={<ResetPasswordToken />} />
        </Route>

        <Route path="/admin" element={<AdminLayout />}>
          <Route path="/admin/dashboard" element={<Dashboard />} />
          <Route path="/admin/boards">
            <Route index element={<BoardsList />} />
            <Route
              path="/admin/boards/:boardId"
              shouldRevalidate={false}
              element={<BoardDetailsLayout />}
            >
              <Route index element={<BoardDetails />} />
              <Route
                path="/admin/boards/:boardId/list"
                element={<BoardDetails />}
              />
              <Route
                path="/admin/boards/:boardId/roadmap"
                element={<BoardRoadmap />}
              />
              <Route
                path="/admin/boards/:boardId/releases"
                element={<BoardReleases />}
              />
              <Route
                path="/admin/boards/:boardId/widget"
                element={<BoardWidget />}
              />
              <Route
                path="/admin/boards/:boardId/settings"
                element={<BoardSettings />}
              />
            </Route>
          </Route>
          <Route path="/admin/users" element={<UsersList />} />
          <Route path="/admin/messaging" element={<MessagingLayout />}>
            <Route index element={<SmsSending />} />
            <Route path="/admin/messaging/sms" element={<SmsSending />} />
            <Route path="/admin/messaging/sms/new" element={<NewSmsJob />} />
            <Route path="/admin/messaging/contacts" element={<Contacts />} />
          </Route>
          <Route path="/admin/subscription" element={<Subscription />} />
          <Route path="/admin/settings" element={<SettingsLayout />}>
            <Route index element={<ProfileSettings />} />
            <Route
              path="/admin/settings/company"
              element={<CompanySettings />}
            >
              <Route
                index
                element={
                  <Navigate to="/admin/settings/company/members" replace />
                }
              />
              <Route
                path="/admin/settings/company/members"
                element={<CompanyMembers />}
              />
            </Route>
          </Route>
        </Route>

        <Route
          path="/public/:companySlug/:boardSlug"
          element={<PublicView />}
        />
        <Route path="/:companySlug/:boardSlug" element={<PublicView />} />
        <Route path="/*" element={<NotFound />} />
      </Route>,
    ),
  );

  return <RouterProvider router={router} />;
}

export default App;
